import { useEffect, useState } from 'react'
import { ENV } from '@forwardflow/helpers/configs'

/**
 * Hook for establishing and managing a Server-Sent Events connection
 * for real-time conversation updates
 */
export const useDemoConversationSSE = () => {
   const [data, setData] = useState<any>(null)
   const [isConnected, setIsConnected] = useState(false)
   const [error, setError] = useState<string | null>(null)
   
   useEffect(() => {
       let eventSource: EventSource | null = null
       let retryCount = 0
       const maxRetryCount = 10
       const baseRetryDelay = 1000
       const maxRetryDelay = 30000

       const createEventSource = () => {
         if (eventSource) {
            eventSource.close()
         }

         const url = `${ENV.PUBLIC_FF_URL}sse/demo-conversation`
         eventSource = new EventSource(url)

         eventSource.onopen = () => {
            console.log('SSE connection established')
            setIsConnected(true)
            setError(null)
            retryCount = 0
         }

         eventSource.addEventListener('data-update', (event) => {
            try {
               const receivedData = JSON.parse(event.data)
               console.log('Received SSE data:', receivedData)
               setData(receivedData) // Store the actual data
            } catch (error) {
               console.error('Error parsing SSE event data:', error)
               setError('Error parsing received data')
            }
         })

         eventSource.addEventListener('message', (event) => {
            try {
               const receivedData = JSON.parse(event.data)
               console.log('Received SSE message:', receivedData)
               setData(receivedData) // Store the actual data
            } catch (error) {
               console.error('Error parsing SSE message:', error)
               setError('Error parsing received message')
            }
         })

         eventSource.onerror = (err) => {
            console.error('SSE connection error:', err)
            setIsConnected(false)
            setError('Connection error')
            
            if (eventSource) {
               eventSource.close()
               eventSource = null
            }

            if (retryCount < maxRetryCount) {
               const delay = Math.min(
                  baseRetryDelay * Math.pow(2, retryCount),
                  maxRetryDelay
               )
               console.log(`Retrying SSE connection in ${delay}ms (attempt ${retryCount + 1})`)
               
               setTimeout(() => {
                  retryCount++
                  createEventSource()
               }, delay)
            } else {
               setError(`Failed to reconnect after ${maxRetryCount} attempts`)
            }
         }

         return eventSource
      }

      createEventSource()

      return () => {
         if (eventSource) {
            eventSource.close()
            eventSource = null
         }
         setIsConnected(false)
      }
   }, [])

   return { data, isConnected, error }
}